import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  MessageCircle,
  Calendar,
  Bell,
  BellOff,
  Loader2,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabase";
import {
  getMediaBuyerDashboard,
  subscribeToConversationUpdates,
  unsubscribeFromDashboard,
  sendMessage,
  markMessagesAsRead,
} from "../../lib/chatService";
import { useDashboardCleanup } from "../../hooks/useChatCleanup";
import ChatConversation from "../chat/ChatConversation";

const MediaBuyerChatDashboard = () => {
  const { user } = useAuth();

  // Core dashboard state
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter and search state
  const [searchTerm, setSearchTerm] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [isOnline, setIsOnline] = useState(true);
  const [realtimeStatus, setRealtimeStatus] = useState("initializing");
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  // Enhanced affiliate search state
  const [affiliateName, setAffiliateName] = useState("");
  const [allAffiliates, setAllAffiliates] = useState([]);
  const [loadingAffiliates, setLoadingAffiliates] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [showAffiliateDropdown, setShowAffiliateDropdown] = useState(false);

  // Message modal state
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState("holistic_hercules");
  const [newMessage, setNewMessage] = useState("");
  const [startingConversation, setStartingConversation] = useState(false);

  // Ensure proper cleanup of dashboard-specific subscriptions
  useDashboardCleanup(user?.id);

  // Handle network status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Search affiliates in database
  const searchAffiliates = useCallback(async (searchTerm) => {
    if (!searchTerm.trim()) {
      setAllAffiliates([]);
      return;
    }

    try {
      setLoadingAffiliates(true);

      console.log(`🔍 Searching for affiliates: "${searchTerm}"`);

      // Search in profiles table for affiliates
      const { data, error } = await supabase
        .from("profiles")
        .select("id, username, full_name, email, user_type")
        .or(
          `username.ilike.%${searchTerm}%,full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`
        )
        .eq("user_type", "affiliate")
        .limit(10);

      if (error) {
        console.error("❌ Error searching affiliates:", error);
        setAllAffiliates([]);
        return;
      }

      const affiliates = (data || []).map((profile) => ({
        id: profile.id,
        name: profile.full_name || profile.username,
        username: profile.username,
        email: profile.email,
      }));

      console.log(`✅ Found ${affiliates.length} affiliates`);
      setAllAffiliates(affiliates);
    } catch (error) {
      console.error("❌ Error searching affiliates:", error);
      setAllAffiliates([]);
    } finally {
      setLoadingAffiliates(false);
    }
  }, []);

  // Debounced search function
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (affiliateName) {
        searchAffiliates(affiliateName);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [affiliateName, searchAffiliates]);

  // Handle clicking outside affiliate dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showAffiliateDropdown &&
        !event.target.closest(".affiliate-search-container")
      ) {
        setShowAffiliateDropdown(false);
      }
    };

    if (showAffiliateDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showAffiliateDropdown]);

  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      console.log(`🚀 Loading dashboard for media buyer: ${user.id}`);

      const data = await getMediaBuyerDashboard(user.id, {
        brand: brandFilter,
        status: statusFilter,
        date: dateFilter,
        search: searchTerm,
      });

      // Deduplicate conversations by ID to prevent React key warnings
      const uniqueConversations = (data.conversations || []).reduce(
        (acc, conversation) => {
          const key = conversation.conversation_id || conversation.id;
          if (key && !acc.find((c) => (c.conversation_id || c.id) === key)) {
            acc.push(conversation);
          }
          return acc;
        },
        []
      );

      console.log(`✅ Dashboard loaded:`, {
        originalCount: data.conversations?.length || 0,
        uniqueCount: uniqueConversations.length,
        sampleConversation: uniqueConversations[0],
        duplicatesRemoved:
          (data.conversations?.length || 0) - uniqueConversations.length,
      });

      setConversations(uniqueConversations);
    } catch (err) {
      console.error(`❌ Error loading dashboard:`, err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id, brandFilter, statusFilter, dateFilter, searchTerm]);

  // Handle conversation selection with immediate unread clearing
  const handleSelectConversation = useCallback(
    async (conversation) => {
      console.log("📋 Selecting conversation:", conversation.id);

      // Set selected conversation immediately
      setSelectedConversation(conversation);

      // If conversation has unread messages, mark them as read
      if (conversation.unread_count > 0 && user?.id) {
        try {
          const conversationId =
            conversation.conversation_id || conversation.id;
          console.log(
            `📖 Marking ${conversation.unread_count} messages as read for conversation: ${conversationId}`
          );

          // Mark messages as read in database
          await markMessagesAsRead(conversationId, user.id);

          // Update local state immediately for better UX
          setConversations((prev) =>
            prev.map((conv) =>
              conv.id === conversation.id
                ? {
                    ...conv,
                    unread_count: 0,
                    last_read_at: new Date().toISOString(),
                  }
                : conv
            )
          );

          console.log("✅ Messages marked as read and UI updated");
        } catch (error) {
          console.error("❌ Error marking messages as read:", error);
          // Don't block the conversation selection if this fails
        }
      }
    },
    [user?.id]
  );

  // Initial load and refresh
  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Subscribe to conversation updates (background process)
  useEffect(() => {
    if (!user?.id) return;

    console.log(`🔄 Setting up real-time updates (background)`);
    setRealtimeStatus("connecting");

    // Auto-connect after 3 seconds to avoid stuck "connecting" state
    const autoConnectTimeout = setTimeout(() => {
      console.log("🔄 Auto-setting realtime status to connected (timeout)");
      setRealtimeStatus((current) =>
        current === "connecting" ? "connected" : current
      );
    }, 3000);

    let unsubscribe;
    try {
      unsubscribe = subscribeToConversationUpdates(
        user.id,
        (updatedConversation) => {
          console.log(`📨 Live update received:`, updatedConversation);

          // The subscription sends the raw database row, need to extract the correct ID
          const conversationId =
            updatedConversation.new?.id || updatedConversation.id;
          const newData = updatedConversation.new || updatedConversation;

          console.log(
            `🔍 Processing update for conversation ID: ${conversationId}`
          );
          console.log(`🔍 New unread_count: ${newData.unread_count}`);

          if (conversationId) {
            setConversations((prev) =>
              prev.map((conv) =>
                conv.id === conversationId
                  ? {
                      ...conv,
                      ...newData,
                      unread_count: newData.unread_count || 0,
                    }
                  : conv
              )
            );
            console.log(
              `✅ Updated conversation ${conversationId} in local state`
            );
          } else {
            console.warn("⚠️ Could not determine conversation ID from update");
          }

          // Update status to connected when we get updates
          setRealtimeStatus("connected");
        },
        (error) => {
          console.error(`❌ Realtime subscription error:`, error);
          setRealtimeStatus("error");
        }
      );

      // Ensure we have a valid unsubscribe function
      if (typeof unsubscribe !== "function") {
        console.warn("⚠️ Subscription did not return a cleanup function");
        setRealtimeStatus("error");
        unsubscribe = null;
      }
    } catch (error) {
      console.error("❌ Failed to subscribe to conversation updates:", error);
      setRealtimeStatus("error");
      unsubscribe = null;
    }

    return () => {
      console.log(`🔌 Unsubscribing from conversation updates`);
      clearTimeout(autoConnectTimeout);
      if (typeof unsubscribe === "function") {
        unsubscribe();
      } else {
        console.warn("⚠️ Unsubscribe function not available");
      }
      setRealtimeStatus("initializing");
    };
  }, [user?.id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (user?.id) {
        try {
          unsubscribeFromDashboard(user.id);
        } catch (error) {
          console.warn("⚠️ Error during dashboard cleanup:", error);
        }
      }
    };
  }, [user?.id]);

  // Handle notifications
  useEffect(() => {
    if (notificationsEnabled && "Notification" in window) {
      Notification.requestPermission().then((permission) => {
        console.log(`🔔 Notification permission: ${permission}`);
      });
    }
  }, [notificationsEnabled]);

  // Handle starting a new conversation for a specific submission
  const handleStartNewConversation = async () => {
    if (!selectedAffiliate || !newMessage.trim()) {
      alert("Please select an affiliate and enter a message");
      return;
    }

    setStartingConversation(true);
    try {
      console.log(
        `🚀 Starting new submission conversation with ${selectedAffiliate.name}`
      );

      // Create submission-specific conversation
      const submissionDate = new Date().toISOString().split("T")[0]; // Today's date
      const submissionTitle = `Submission ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;

      // 1. Create or get submission-specific conversation
      const { data, error } = await supabase.rpc(
        "get_or_create_conversation_submission_specific",
        {
          p_affiliate_id: selectedAffiliate.id,
          p_brand: selectedBrand,
          p_submission_date: submissionDate,
          p_submission_title: submissionTitle,
          p_media_buyer_id: user.id,
        }
      );

      if (error) {
        console.error("❌ Error creating submission conversation:", error);
        alert("Failed to create conversation. Please try again.");
        return;
      }

      const conversation = data[0];
      if (!conversation || !conversation.conversation_id) {
        alert("Failed to create conversation. Please try again.");
        return;
      }

      // 2. Send the initial message
      await sendMessage(
        conversation.conversation_id,
        user.id,
        newMessage.trim(),
        "text"
      );

      // 3. Clear form and close modal
      setShowMessageModal(false);
      setAffiliateName("");
      setSelectedAffiliate(null);
      setShowAffiliateDropdown(false);
      setNewMessage("");

      // 4. Refresh the dashboard to show new conversation
      await loadDashboard();

      console.log(
        `✅ Successfully started submission conversation: ${submissionTitle} with ${selectedAffiliate.username}`
      );
    } catch (error) {
      console.error("❌ Error starting conversation:", error);
      alert(`Failed to start conversation: ${error.message}`);
    } finally {
      setStartingConversation(false);
    }
  };

  // Filter conversations based on search and filters
  const filteredConversations = conversations.filter((conversation) => {
    const matchesSearch =
      !searchTerm ||
      conversation.affiliate?.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      conversation.affiliate?.username
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      conversation.submission_title
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      conversation.last_message_text
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesBrand = !brandFilter || conversation.brand === brandFilter;
    const matchesStatus = !statusFilter || conversation.status === statusFilter;

    const matchesDate =
      !dateFilter ||
      (conversation.submission_date &&
        new Date(conversation.submission_date).toDateString() ===
          new Date(dateFilter).toDateString()) ||
      (conversation.last_message_at &&
        new Date(conversation.last_message_at).toDateString() ===
          new Date(dateFilter).toDateString());

    return matchesSearch && matchesBrand && matchesStatus && matchesDate;
  });

  // Connection status component (minimal UI)
  const ConnectionStatus = () => (
    <div className="flex items-center gap-2 text-xs">
      {isOnline ? (
        <div className="flex items-center gap-1 text-green-400">
          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
          <span>Online</span>
        </div>
      ) : (
        <div className="flex items-center gap-1 text-red-400">
          <AlertCircle className="w-3 h-3" />
          <span>Offline</span>
        </div>
      )}

      {/* Only show real-time status when it's relevant */}
      {realtimeStatus === "connected" && (
        <>
          <span className="text-gray-400">•</span>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <span className="text-green-400 text-xs">Live Updates</span>
          </div>
        </>
      )}

      {realtimeStatus === "error" && (
        <>
          <span className="text-gray-400">•</span>
          <div className="flex items-center gap-1">
            <AlertCircle className="w-3 h-3 text-orange-400" />
            <span className="text-orange-400 text-xs">Live Updates Off</span>
          </div>
        </>
      )}

      {/* Hide "connecting" and "initializing" states - they happen in background */}
    </div>
  );

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-black via-gray-900 to-black">
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-8 text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#B8460E] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">
            Loading Dashboard
          </h2>
          <p className="text-gray-400">Fetching your conversations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-black via-gray-900 to-black">
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-8 text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">
            Error Loading Dashboard
          </h2>
          <p className="text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#B8460E] hover:bg-[#8B3A0E] text-white px-6 py-2 rounded-lg transition-colors duration-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex overflow-hidden bg-gradient-to-br from-black via-gray-900 to-black text-white">
      {/* Sidebar - Conversation List */}
      <div
        className={`${selectedConversation ? "hidden md:flex" : "flex"} w-full md:w-80 bg-white/5 backdrop-blur-xl border-r border-white/10 flex-col`}
      >
        {/* Header */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-white">Media Buyer Chat</h1>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                title={
                  notificationsEnabled
                    ? "Disable notifications"
                    : "Enable notifications"
                }
              >
                {notificationsEnabled ? (
                  <Bell className="h-4 w-4 text-white" />
                ) : (
                  <BellOff className="h-4 w-4 text-gray-400" />
                )}
              </button>
              <button
                onClick={loadDashboard}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                title="Refresh conversations"
              >
                <RefreshCw className="h-4 w-4 text-white" />
              </button>
            </div>
          </div>

          <ConnectionStatus />
        </div>

        {/* Search and Filters */}
        <div className="p-4 border-b border-white/10 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <select
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
            >
              <option value="">All Brands</option>
              <option value="holistic_hercules">Holistic Hercules</option>
              <option value="aphrodite">Aphrodite</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="unread">Unread</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              placeholder="Filter by submission date"
              title="Filter by submission date"
              className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
            />
          </div>
        </div>

        {/* Start New Conversation */}
        <div className="border-b border-white/10 p-3">
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-white">
              Start New Conversation
            </h3>

            {/* Affiliate Search Input */}
            <div className="relative affiliate-search-container">
              <div className="relative">
                <input
                  type="text"
                  value={affiliateName}
                  onChange={(e) => {
                    setAffiliateName(e.target.value);
                    setSelectedAffiliate(null);
                    setShowAffiliateDropdown(true);
                  }}
                  onFocus={() => setShowAffiliateDropdown(true)}
                  placeholder="Search affiliate by name, username, or email..."
                  className="w-full px-3 py-2 pr-10 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
                />
                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              </div>

              {/* Affiliate Dropdown */}
              {showAffiliateDropdown && affiliateName && (
                <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-gray-800 border border-white/20 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                  {loadingAffiliates ? (
                    <div className="p-3 text-center text-gray-400 text-sm">
                      <Loader2 className="h-4 w-4 animate-spin mx-auto mb-1" />
                      Searching affiliates...
                    </div>
                  ) : allAffiliates.length > 0 ? (
                    allAffiliates.slice(0, 10).map((affiliate) => (
                      <button
                        key={affiliate.id}
                        onClick={() => {
                          setSelectedAffiliate(affiliate);
                          setShowAffiliateDropdown(false);
                          setShowMessageModal(true);
                          setAffiliateName("");
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-white/10 text-white text-sm border-b border-white/10 last:border-b-0"
                      >
                        <div className="font-medium">{affiliate.name}</div>
                        <div className="text-xs text-gray-400">
                          @{affiliate.username}
                          {affiliate.email && ` • ${affiliate.email}`}
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="p-3 text-center text-gray-400 text-sm">
                      No affiliates found matching "{affiliateName}"
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {filteredConversations.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-gray-300">No conversations yet</p>
              <p className="text-gray-500 text-sm mt-1">
                Media buyers will message you here if there is a problem with
                your creatives.
              </p>
            </div>
          ) : (
            <div className="p-2 space-y-2">
              {filteredConversations.map((conversation, index) => {
                const conversationKey =
                  conversation.conversation_id ||
                  conversation.id ||
                  `conversation-${index}`;
                return (
                  <button
                    key={conversationKey}
                    onClick={() => handleSelectConversation(conversation)}
                    className={`w-full p-3 rounded-lg text-left transition-colors ${
                      selectedConversation?.id === conversation.id
                        ? "bg-[#B8460E]/20 border border-[#B8460E]/30"
                        : "bg-white/5 hover:bg-white/10 border border-white/10"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-white text-sm truncate">
                          {conversation.submission_title ||
                            conversation.affiliate?.name ||
                            "Unknown Affiliate"}
                        </h3>
                        <p className="text-gray-400 text-xs">
                          {conversation.affiliate?.name || "Unknown"} • @
                          {conversation.affiliate?.username || "unknown"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${
                            conversation.brand === "holistic_hercules"
                              ? "bg-purple-500/20 text-purple-300"
                              : "bg-pink-500/20 text-pink-300"
                          }`}
                        >
                          {conversation.brand === "holistic_hercules"
                            ? "HH"
                            : "APH"}
                        </span>
                        {conversation.unread_count > 0 && (
                          <span className="bg-[#B8460E] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                            {conversation.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-gray-300 text-sm line-clamp-2 mb-1">
                      {conversation.last_message_text || "No messages yet"}
                    </p>
                    <div className="flex items-center justify-between text-xs">
                      <p className="text-gray-500">
                        {conversation.submission_date
                          ? `Submission: ${new Date(conversation.submission_date).toLocaleDateString()}`
                          : "No submission date"}
                      </p>
                      <p className="text-gray-500">
                        {conversation.last_message_at
                          ? new Date(
                              conversation.last_message_at
                            ).toLocaleDateString()
                          : ""}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div
        className={`${selectedConversation ? "flex" : "hidden md:flex"} flex-1 flex-col min-h-0`}
      >
        {selectedConversation ? (
          (() => {
            const conversationId =
              selectedConversation.conversation_id || selectedConversation.id;

            console.log("🔍 Selected conversation data:", {
              id: selectedConversation.id,
              conversation_id: selectedConversation.conversation_id,
              finalConversationId: conversationId,
            });

            if (!conversationId) {
              return (
                <div className="flex-1 flex items-center justify-center p-8">
                  <div className="text-center max-w-md">
                    <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-white mb-2">
                      Invalid Conversation
                    </h2>
                    <p className="text-gray-400 mb-6">
                      This conversation has an invalid ID. Please try selecting
                      another conversation.
                    </p>
                    <button
                      onClick={() => setSelectedConversation(null)}
                      className="bg-[#B8460E] hover:bg-[#8B3A0E] text-white px-6 py-2 rounded-lg transition-colors duration-200"
                    >
                      Go Back
                    </button>
                  </div>
                </div>
              );
            }

            return (
              <>
                {/* Mobile back header */}
                <div className="md:hidden flex items-center gap-3 p-3 border-b border-white/10 bg-white/5">
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="flex items-center gap-2 text-white bg-white/10 hover:bg-white/20 border border-white/20 px-3 py-1.5 rounded-lg"
                  >
                    <span className="text-sm">Chats</span>
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold truncate">
                      {selectedConversation.affiliate?.name ||
                        selectedConversation.submission_title ||
                        "Conversation"}
                    </p>
                    <p className="text-xs text-gray-400 capitalize truncate">
                      {selectedConversation.brand?.replace("_", " ")}
                    </p>
                  </div>
                </div>
                <ChatConversation
                  conversationId={conversationId}
                  brand={selectedConversation.brand}
                  onBack={() => setSelectedConversation(null)}
                />
              </>
            );
          })()
        ) : (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center max-w-md">
              <p className="text-gray-300">No conversations yet</p>
              <p className="text-gray-500 text-sm mt-1">
                Media buyers will message you here if there is a problem with
                your creatives.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Message Modal */}
      {showMessageModal && selectedAffiliate && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl max-w-md w-full">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-[#B8460E] rounded-lg">
                  <MessageCircle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    New Submission Chat
                  </h2>
                  <p className="text-gray-400 text-sm">
                    To: {selectedAffiliate.name} (@{selectedAffiliate.username})
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    Creates a new conversation for today's submission
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowMessageModal(false);
                  setSelectedAffiliate(null);
                  setNewMessage("");
                  setAffiliateName("");
                }}
                className="text-gray-400 hover:text-white text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors"
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {/* Brand Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Brand
                </label>
                <select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
                >
                  <option value="holistic_hercules">Holistic Hercules</option>
                  <option value="aphrodite">Aphrodite</option>
                </select>
              </div>

              {/* Message Input */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Message
                </label>
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Write your message to the affiliate..."
                  rows={4}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent resize-none"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowMessageModal(false);
                    setSelectedAffiliate(null);
                    setNewMessage("");
                    setAffiliateName("");
                  }}
                  className="flex-1 px-4 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStartNewConversation}
                  disabled={!newMessage.trim() || startingConversation}
                  className={`flex-1 px-4 py-3 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                    newMessage.trim() && !startingConversation
                      ? "bg-[#B8460E] hover:bg-[#8B3A0E] text-white"
                      : "bg-gray-600 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  {startingConversation ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <MessageCircle className="h-4 w-4" />
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaBuyerChatDashboard;
